import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import Collapse from '@rio-cloud/rio-uikit/Collapse';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';
import ToggleButton from '@rio-cloud/rio-uikit/ToggleButton';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { RentalAssetData } from '../../domain/model/rental-asset.types';
import type { RenterPartnerCompany } from '../../domain/model/rental-companies.types';
import { RENTAL_TYPE, type RentalType } from '../../domain/model/rentals.types';
import RenterCompanyAutoSuggestion, { type RenterCompanySelection } from './RenterCompanyAutoSuggestion';
import VehicleGrid from './VehicleGrid';

type StartRentalDialogBodyProps = {
    selectedRentalAssetData: RentalAssetData[];
    renterPartnerCompanies: RenterPartnerCompany[];
    setSelectedAccountId: (value: string | null, selection?: RenterCompanySelection) => void;
    setRenterIdentifier: (value: string | null) => void;
    selectedOption: RentalType;
    setSelectedOption: (value: RentalType) => void;
};

const StartRentalDialogBody = (props: StartRentalDialogBodyProps) => {
    const {
        selectedRentalAssetData,
        renterPartnerCompanies,
        setSelectedAccountId,
        setRenterIdentifier,
        setSelectedOption,
        selectedOption,
    } = props;

    const intl = useIntl();
    const [showVatAccountLookupDescription, setShowVatAccountLookupDescription] = useState(false);

    return (
        <div className='display-flex gap-20 padding-x-25 padding-bottom-25'>
            <div className='width-250 display-flex flex-column items-center padding-top-25 padding-right-15'>
                <img src='https://cdn.rio.cloud/riosvg/custom/time-start.svg' alt='' />
            </div>
            <div>
                <p>
                    <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.content' />
                </p>

                <div className='margin-top-15'>
                    <div className='display-flex flex-column'>
                        <RadioButton
                            name='stackedRadioBoxes'
                            custom
                            className='padding-y-15'
                            data-testid='radio-select-account-rental'
                            onChange={() => setSelectedOption(RENTAL_TYPE.ACCOUNT)}
                            checked={selectedOption === RENTAL_TYPE.ACCOUNT}
                        >
                            <div className='radio-text-wrapper display-flex'>
                                <span className='radio-text' />
                                <div className='margin-left-5 margin-right-5'>
                                    <div className='text-medium text-size-16'>
                                        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.account-rental.title' />
                                    </div>
                                    <div className='text-color-dark'>
                                        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.account-rental.description' />
                                    </div>
                                    <div>
                                        <ToggleButton
                                            className='margin-top-5 margin-bottom-10'
                                            active={showVatAccountLookupDescription}
                                            bsStyle='info'
                                            onClick={show => setShowVatAccountLookupDescription(show)}
                                            variant='link'
                                        >
                                            <span className='rioglyph rioglyph-info-sign margin-right-5' />
                                            <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.account-rental.description.vat.toggle' />
                                        </ToggleButton>
                                    </div>
                                    <Collapse open={showVatAccountLookupDescription} unmountOnExit>
                                        <div className='panel-wrapper'>
                                            <div className='panel panel-default'>
                                                <div className='panel-body text-color-dark'>
                                                    <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.account-rental.description.vat' />
                                                </div>
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                            </div>
                        </RadioButton>

                        <RadioButton
                            name='stackedRadioBoxes'
                            custom
                            className='padding-y-15'
                            data-testid='radio-select-external-rental'
                            onChange={() => setSelectedOption(RENTAL_TYPE.EXTERNAL)}
                            checked={selectedOption === RENTAL_TYPE.EXTERNAL}
                        >
                            <div className='radio-text-wrapper display-flex'>
                                <span className='radio-text' />
                                <div className='margin-left-5 margin-right-5'>
                                    <div className='text-medium text-size-16'>
                                        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.external-rental.title' />
                                    </div>
                                    <div className='text-color-dark'>
                                        <FormattedMessage
                                            id='intl-msg:rental-web.dialog.start-rental.option.external-rental.description'
                                            values={{
                                                i: chunk => <i>{chunk}</i>,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </RadioButton>
                    </div>
                </div>

                <div className='margin-top-10'>
                    <div className='form-group'>
                        <FormLabel htmlFor='pick-up-company'>
                            <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.renter-company-auto-suggestion.title' />
                        </FormLabel>

                        {selectedOption === RENTAL_TYPE.ACCOUNT && (
                            <RenterCompanyAutoSuggestion
                                id='pick-up-company'
                                renterPartnerCompanies={renterPartnerCompanies}
                                onChanged={(value, selection) => {
                                    setSelectedAccountId(value ?? null, selection);
                                }}
                            />
                        )}

                        {selectedOption === RENTAL_TYPE.EXTERNAL && (
                            <div className='input-group'>
                                <span className='input-group-addon'>
                                    <span className='rioglyph-icon-pair text-size-18 text-color-gray'>
                                        <span className='rioglyph rioglyph-building' />
                                        <span className='rioglyph rioglyph-share scale-105' />
                                    </span>
                                </span>
                                <ClearableInput
                                    id='pick-up-company'
                                    placeholder={intl.formatMessage({
                                        id: 'intl-msg:rental-web.dialog.start-rental.external-rental.input.placeholder',
                                    })}
                                    onChange={(value: string) => {
                                        setRenterIdentifier(value);
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className='margin-top-25'>
                        <FormLabel>
                            <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.vehicle-selection.label' />
                        </FormLabel>
                        <VehicleGrid rentalAssetData={selectedRentalAssetData} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StartRentalDialogBody;
